/** @prettier */
import classNames from 'classnames';
import { List, Record } from 'immutable';
import PropTypes from 'prop-types';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import MBZeroSportTiles from '@xanadu/ui-shared/components/MBZeroSportTiles';
import { EMPTY_LIST, PRODUCTS, PRODUCT_LINKS, RACING_SPORTS } from '@xanadu/ui-shared/constants';
import ChildrenPropsContext from '@xanadu/ui-shared/contexts/ChildrenPropsContext';
import { useImprovedTranslation, useSports } from '@xanadu/ui-shared/hooks';
import { isFuturesOrPredictionsProduct } from '@xanadu/ui-shared/modules/application/selectors';
import { toggleNavigation as toggleNavigationAct } from '@xanadu/ui-shared/modules/navigation/actions';
import navigateExternal from '@xanadu/ui-shared/utils/navigateExternal';

import { getClientStyledText } from '../../utils';

import ArrowDown from '../../../../images/icons/misc/Arrow-Down.svg';

import SearchBar from '../Header/SearchBar/SearchBar';
import LanguageSelector from '../Header/UserButtons/SettingsDrop/LanguageSelector/LanguageSelector';
import OddsTypeSelector from '../Header/UserButtons/SettingsDrop/OddsTypeSelector/OddsTypeSelector';
import SearchResults from '../SearchResults';

import styles from './MobileNavigation.module.scss';
import Tile from './Tile';

export const NAVIGATION_INTERACTION_TIMEOUT = 270;

const MobileNavigation = ({
  product,
  tags,
  topFive,
  inPlayTags,
  history,
  toggleNavigation,
  navigationVisible,
  casinoVisibilityRestricted,
  virtualsAllowed,
  isFuturesOrPredictions = false,
  additionalDisplayedVerticals,
  brand,
  site,
  clientString = '',
}) => {
  const {
    featuresFlags: {
      subProductsEnabled = [],
      externalProductsEnabled = [],
      singleOddsType = false,
      displayTimeZoneOnRace = false,
    } = {},
    customization: {
      MobileNavigation: {
        displaySettingsSection = false,
        displayLanguageSelector = false,
        displayOddsTypeSelector = false,
        displayMoreProductsSection = false,
        displayExternalBanners = true,
      } = {},
    } = {},
  } = React.useContext(ChildrenPropsContext) || {};

  const isExchange = product === PRODUCTS.exchange;
  const isMBZero = product === PRODUCTS.mbzero;

  const [searchFocused, setSearchFocus] = useState(false);
  const [settingsExpanded, setSettingsExpanded] = useState(false);

  const { t } = useImprovedTranslation();

  const allSports = useSports({ tags, isExchange });

  const showSearch = isExchange || isFuturesOrPredictions;

  const navigate = useCallback(({ url }) => {
    toggleNavigation(false);

    const timer = setTimeout(() => {
      history.push(url);
    }, NAVIGATION_INTERACTION_TIMEOUT);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  const onSearchResultClick = useCallback(() => {
    toggleNavigation(false);
  }, []);

  const StyledText = getClientStyledText(brand, site);

  const externalSections = useMemo(
    () =>
      additionalDisplayedVerticals
        .filter(({ label }) =>
          externalProductsEnabled.some((externalLink) => externalLink.name === label)
        )
        .map(({ label, url, banner }) => (
          <div key={label}>
            <div className={styles.sectionHeader}>
              <div className={styles.externalVHeader}>
                {StyledText}
                <span>{label}</span>
              </div>
            </div>
            <div
              className={styles.sectionBanner}
              onClick={() => navigateExternal({ url, callback: toggleNavigation })}
              role="button"
            >
              <img src={banner} alt={`${label}_banner`} />
            </div>
          </div>
        )),
    [additionalDisplayedVerticals, externalProductsEnabled]
  );

  const onSearchFocusChange = (searchFocus) => {
    setSearchFocus(searchFocus);
  };

  const getSportDataHook = (urlName) => {
    const sportDataHooks = {
      soccer: 'mob-nav-soccer',
      'horse-racing': 'mob-nav-horse-racing',
      baseball: 'mob-nav-baseball',
    };
    return sportDataHooks[urlName];
  };

  const products = useMemo(() => {
    const filteredProducts = subProductsEnabled.reduce((update, id) => {
      const externalProduct = externalProductsEnabled.find(
        (externalLink) => externalLink.id === id
      );

      if (externalProduct) {
        const alreadyShownAsVertical = additionalDisplayedVerticals.some(
          (vertical) => vertical.label === externalProduct.name
        );

        return alreadyShownAsVertical ? update : [...update, externalProduct];
      }

      const productLink = PRODUCT_LINKS.find((link) => link.id === id);

      if (!productLink) {
        return update;
      }

      if (productLink.id === PRODUCTS.virtuals) {
        return virtualsAllowed && product !== PRODUCTS.virtuals ? [...update, productLink] : update;
      }

      if (casinoVisibilityRestricted) {
        return [PRODUCTS.casino, PRODUCTS.liveCasino].includes(productLink.id)
          ? update
          : [...update, productLink];
      }

      return productLink.id !== product ? [...update, productLink] : update;
    }, []);

    return filteredProducts.map(({ id, name, urlName, url, external }) => (
      <Tile
        key={id}
        product={id}
        name={name}
        iconName={urlName || PRODUCTS.exchange}
        url={url}
        onClick={external ? () => navigateExternal({ url, callback: toggleNavigation }) : navigate}
        dataHook={`mob-nav-${id}`}
        className=".gtm_cta"
        showLabel
      />
    ));
  }, [product, virtualsAllowed, casinoVisibilityRestricted, additionalDisplayedVerticals]);

  let topFiveSports = [];

  if (tags.size) {
    topFiveSports = topFive.slice(0, 5).reduce((acc, item) => {
      const existingTag = tags.find((tag) => tag.id === item.id);
      const { children = [], url, urlName } = existingTag || new Record();
      const { url: firstSubMetaTagUrlName } = tags.find((tag) => tag.uuid === children[0]) || {};

      const setFirstCompetitionUrl = !RACING_SPORTS.includes(urlName) && children.length === 1;

      if (existingTag) {
        return [
          ...acc,
          existingTag.set('url', setFirstCompetitionUrl ? firstSubMetaTagUrlName : url),
        ];
      }

      return acc;
    }, []);

    if (inPlayTags.size) {
      topFiveSports.push({
        urlName: 'live-betting',
        id: 'live-betting',
        name: 'Live Betting',
        url: '/live-betting',
      });
    }
  }

  useEffect(() => {
    document.documentElement.style.overflow = navigationVisible ? 'hidden' : '';

    return () => {
      document.documentElement.style.overflow = '';
    };
  }, [navigationVisible]);

  const mainClass = classNames({
    [styles.main]: true,
    [styles['main--opened']]: navigationVisible,
    [styles['main--noSearch']]: !showSearch,
  });

  const headerClass = classNames({
    [styles.sectionHeader]: true,
    [styles['sectionHeader--spaced']]: true,
  });

  const spacedContentClass = classNames({
    [styles.sectionContent]: true,
    [styles.sectionSpaced]: true,
  });

  const arrowContainerClass = classNames({
    [styles.expandArrow]: true,
    [styles['expandArrow--rotated']]: settingsExpanded,
  });

  const startAlignedSectionClass = classNames({
    [styles.sectionContent]: true,
    [styles.rowStart]: true,
  });

  const translatedJackpotTitle = useMemo(
    () => <Trans i18nKey="Jackpots brand" values={{ brand: clientString }} />,
    [clientString]
  );

  const translatedMoreFromBrandTitle = useMemo(
    () => <Trans i18nKey="More from brand" values={{ brand: clientString }} />,
    [clientString]
  );

  return (
    <div className={mainClass} data-hook="mobile-navigation-main">
      {navigationVisible && (
        <Fragment>
          {showSearch && (
            <div className={styles.containerSearch}>
              <SearchBar showLoader={false} onInputFocusChange={onSearchFocusChange} />
            </div>
          )}
          {!searchFocused ? (
            <Fragment>
              {(isExchange || isFuturesOrPredictions) && (
                <div>
                  <div className={styles.sectionHeader}>{t('Popular Sports')}</div>
                  <div className={styles.sectionContent}>
                    {topFiveSports.map(({ urlName, id, name, url }) => (
                      <Tile
                        key={id}
                        name={name}
                        iconName={urlName}
                        url={`/events${url}`}
                        onClick={navigate}
                        invertedColors
                      />
                    ))}
                  </div>
                </div>
              )}
              {displayExternalBanners && externalSections}
              {isMBZero && (
                <div className={styles.mbZeroSportTiles}>
                  <div className={styles.sectionHeader}>{t('MBZero Sports')}</div>
                  <MBZeroSportTiles navigateFunction={navigate} />
                </div>
              )}
              {displayMoreProductsSection && Boolean(products.length) && (
                <div data-hook="mob-nav-products">
                  <div className={styles.sectionHeader}>{translatedMoreFromBrandTitle}</div>
                  <div className={startAlignedSectionClass}>{products}</div>
                </div>
              )}
              {(isExchange || isFuturesOrPredictions) && (
                <div>
                  <div className={styles.sectionHeader}>{t('All Sports')}</div>
                  <div className={styles.sectionContent}>
                    {allSports.map(({ id, urlName, url, name }) => (
                      <Tile
                        key={id}
                        // name={name}
                        name={urlName === 'colossus' ? translatedJackpotTitle : name}
                        iconName={urlName}
                        url={urlName === 'colossus' ? '/colossus' : `/events${url}`}
                        onClick={navigate}
                        invertedColors
                        dataHook={getSportDataHook(urlName)}
                      />
                    ))}
                  </div>
                </div>
              )}
              {displaySettingsSection && (
                <div data-hook="navigation_settings_container">
                  <div
                    className={headerClass}
                    role="button"
                    data-hook="navigation_settings_toggle"
                    onClick={() => {
                      setSettingsExpanded(!settingsExpanded);
                    }}
                  >
                    {t('Settings')}
                    <div className={arrowContainerClass}>
                      <ArrowDown />
                    </div>
                  </div>
                  {settingsExpanded ? (
                    <div className={spacedContentClass}>
                      {displayLanguageSelector ? (
                        <div style={{ gridRowStart: '1' }}>
                          <LanguageSelector />
                        </div>
                      ) : null}
                      {displayOddsTypeSelector && !singleOddsType ? (
                        <div style={{ gridRowStart: '2' }}>
                          <OddsTypeSelector />
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              )}
            </Fragment>
          ) : (
            <div className={styles.containerSearchResults}>
              <SearchResults
                isMobile
                onSearchResultClick={onSearchResultClick}
                displayTimeZoneOnRace={displayTimeZoneOnRace}
              />
            </div>
          )}
        </Fragment>
      )}
    </div>
  );
};

MobileNavigation.propTypes = {
  product: PropTypes.string.isRequired,
  navigationVisible: PropTypes.bool.isRequired,
  toggleNavigation: PropTypes.func.isRequired,
  topFive: PropTypes.instanceOf(List).isRequired,
  tags: PropTypes.instanceOf(List).isRequired,
  inPlayTags: PropTypes.instanceOf(List).isRequired,
  casinoVisibilityRestricted: PropTypes.bool,
  isFuturesOrPredictions: PropTypes.bool,
  virtualsAllowed: PropTypes.bool.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
  additionalDisplayedVerticals: PropTypes.array.isRequired,
  brand: PropTypes.string.isRequired,
  site: PropTypes.string.isRequired,
  clientString: PropTypes.string,
};

const mapDispatchToProps = {
  toggleNavigation: toggleNavigationAct,
};

function mapStateToProps(state) {
  return {
    product: state.getIn(['application', 'product']),
    brand: state.getIn(['application', 'brand']),
    site: state.getIn(['application', 'site']),
    tags: state.getIn(['navigation', 'tags'], EMPTY_LIST),
    inPlayTags: state.getIn(['navigation', 'inPlayTags'], EMPTY_LIST),
    topFive: state.getIn(['navigation', 'topFive'], EMPTY_LIST),
    navigationVisible: state.getIn(['navigation', 'navigationVisible']),
    casinoVisibilityRestricted: state.getIn(['vegas', 'visibilityRestricted'], true),
    virtualsAllowed: state.getIn(['user', 'virtualsAllowed']),
    isFuturesOrPredictions: isFuturesOrPredictionsProduct(state),
    additionalDisplayedVerticals: state.getIn(['application', 'additionalDisplayedVerticals']),
  };
}

export const Component = MobileNavigation;

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(MobileNavigation));
